<template>
    <div :key="defaultKey">
        <validation-observer ref="createShareholderValidation">
            <validation-provider
                v-slot="{ errors }"
                name="shareholder type"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group
                    label="Type"
                    :state="errors.length > 0 ? false : null"
                >
                    <v-select
                        ref="shareholderType"
                        v-model="shareholder.shareholderType"
                        class="custom-select-icon text-capitalize"
                        :clearable="false"
                        placeholder="Select shareholder type"
                        label="text"
                        :options="shareholderOptions"
                        @input="handleShareholderTypeChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicIdLabel.toLowerCase()"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="shareholder-id" for="id">
                            {{ dynamicIdLabel }}
                            <vector
                                v-if="isShareHolderTypeIndividual"
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-input
                        v-model="shareholder.nric"
                        :placeholder="dynamicIdPlaceholder"
                        :state="errors.length > 0 ? false : null"
                        data-test="shareholder-id"
                        @input="requestAutoFillFormData"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>

                    <b-popover
                        v-if="isShareHolderTypeIndividual"
                        target="shareholder-id"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter your NRIC or FIN if you are a Singapore resident.
                        <br />
                        Enter your passport number if you are a foreigner.
                    </b-popover>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="shareholder name"
                rules="required"
            >
                <b-form-group label="Legal name">
                    <b-form-input
                        id="name"
                        v-model="shareholder.name"
                        placeholder="Enter shareholder legal name"
                        :state="errors.length > 0 ? false : null"
                        data-test="shareholder-name"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

             <div v-if="isShareHolderTypeIndividual">
                <validation-provider
                    v-slot="{ errors }"
                    name="ID type"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="ID type"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="idType"
                            v-model="shareholder.idType"
                            class="custom-select-icon"
                            :clearable="false"
                            placeholder="Select ID type"
                            label="name"
                            :options="idType_options"
                            @input="handleIdTypeOnChange"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="nationality"
                    rules="required"
                    class="end-align-indicator-long-select"
                    mode="lazy"
                >
                    <b-form-group
                        label="Nationality"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="nationality"
                            v-model="shareholder.nationality"
                            class="custom-select-icon"
                            placeholder="Select nationality"
                            label="text"
                            :clearable="false"
                            :disabled="shouldDisabledNationality"
                            :options="nationality_options"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <div v-if="shouldHideShareholderDateOfExpiry">
                    <validation-provider
                        v-slot="{ errors }"
                        name="date of expiry"
                        rules="required"
                    >
                        <b-form-group :state="errors.length > 0 ? false : null">
                            <slot name="label">
                                <label id="dateOfExpiry" for="id">
                                    Date of expiry
                                    <vector
                                        data-type="icon"
                                        data-width="15px"
                                        data-height="15px"
                                        data-class="mr-.5"
                                        data-name="information"
                                    ></vector>
                                </label>
                            </slot>

                            <b-popover
                                target="dateOfExpiry"
                                triggers="hover"
                                placement="right"
                                variant="black"
                            >
                                Skip this if you are a Singapore citizen or permanent
                                resident.
                                <br />
                                Fill in the date of expiry of your passport or FIN if
                                you are a foreigner.
                            </b-popover>

                            <flat-pickr
                                v-model="shareholder.dateOfExpiry"
                                placeholder="Enter date of expiry"
                                class="form-control"
                                :config="datePickerConfig"
                                data-test="date-picker-doe"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>
             </div>

            <div v-if="isShareHolderTypeCorporate">
                <validation-provider
                    v-slot="{ errors }"
                    name="country of incorporation"
                    rules="required"
                    class="end-align-indicator-long-select"
                    mode="lazy"
                >
                    <b-form-group
                        label="Country of incorporation"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="country"
                            v-model="shareholder.country"
                            class="custom-select-icon"
                            :clearable="false"
                            placeholder="Select country of incorporation"
                            label="text"
                            :options="country_options"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicDobLabel.toLowerCase()"
                rules="required"
            >
                <b-form-group
                    :label="dynamicDobLabel"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        v-model="shareholder.dob"
                        :placeholder="dynamicDobPlaceholder"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-dob"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicEmailLabel.toLowerCase()"
                rules="required|email"
            >
                <b-form-group :label="dynamicEmailLabel">
                    <b-form-input
                        v-model="shareholder.email"
                        :placeholder="dynamicEmailPlaceholder"
                        :state="errors.length > 0 ? false : null"
                        data-test="shareholder-email"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="contact number"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="contact-number" for="contact-number">
                            Contact number
                            <vector
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-input
                        v-model="shareholder.contactNumber"
                        placeholder="Enter shareholder contact number"
                        :state="errors.length > 0 ? false : null"
                        type="tel"
                        data-test="shareholder-contact-number"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <b-popover
                        target="contact-number"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter your Singapore mobile number if you are a Singapore resident.
                        <br />
                        Enter your overseas mobile number if you are a foreigner.
                    </b-popover>
                </b-form-group>
            </validation-provider>
            
            <validation-provider
                v-slot="{ errors }"
                name="number of shares"
                :rules="`required|formation_validate_share:${totalNumberOfShare},${dataStatus},${companyTotalShare},${dataShareholder.noOfShares}`"
            >
                <b-form-group label="Number of shares">
                    <b-form-input
                        v-model="shareholder.noOfShares"
                        v-mask="shareMask"
                        type="text"
                        placeholder="Enter number of shares"
                        :state="errors.length > 0 ? false : null"
                        data-test="noOfShares"
                        no-wheel
                        @keydown.up.prevent
                        @keydown.down.prevent
                        @input="calculateSharePercentage"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <label class="">
                Provide the percentage of shares owned by
                <strong class="share-percent-name text-uppercase">
                    {{ shareholder.name ? shareholder.name : "Shareholder" }}
                </strong>.
            </label>

            <validation-provider
                v-slot="{ errors }"
                name="shareholder percent"
            >
                <div class="container">
                    <vue-slide-bar
                        ref="sharePercentage"
                        v-model="shareholder.sharePercentage"
                        :is-disabled="true"
                        :draggable="false"
                        :range="sliderConfig.range"
                        :process-style="sliderConfig.processStyle"
                        :tooltip-styles="sliderConfig.tooltipStyles"
                    >
                    </vue-slide-bar>
                    <small class="text-danger">{{ errors[0] }}</small>
                </div>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="share type"
                rules="required"
                class="end-align-indicator-long-select disabled-select"
            >
                <b-form-group label="Share type">
                    <v-select
                        ref="shareType"
                        v-model="shareTypeDefaultValue"
                        class="custom-select-icon"
                        :clearable="false"
                        placeholder="Select share type"
                        label="text"
                        readonly
                        disabled
                        :options="shareTypes"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <!-- old reference later to delete -->
            <!-- <div class="mt-2">
                <b-form-group label="Please select all the sources for your company’s funds.">
                    <validation-provider
                        v-slot="{ errors }"
                        name="source of fund"
                        rules="required"
                    >
                        <b-form-checkbox-group
                            id="checkbox-group-2"
                            ref="sourceOfFund"
                            v-model="selectedSourcesOfFunds"
                            name="sources"
                            stacked
                            :options="sourceOfFundsOption"
                        >
                        </b-form-checkbox-group>
                        <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                            {{ errors[0] }}
                        </div>
                    </validation-provider>
                </b-form-group>

                <b-form-group v-if="checkOtherSourceOfFundOption" data-test="other-source">
                    <validation-provider
                        v-slot="{ errors }"
                        name="other sources of fund"
                        rules="required"
                    >
                        <b-form-textarea
                            id="funding_other_reasons"
                            ref="otherSource"
                            v-model="shareholder.otherSource"
                            name="other_source"
                            rows="3"
                            max-rows="3"
                            placeholder="Enter your other source here."
                        ></b-form-textarea>

                        <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                            {{ errors[0] }}
                        </div>
                    </validation-provider>
                </b-form-group>
            </div> -->
            <!-- end old reference -->

            <!-- Source of Fund or source of wealth-->
            <div class="mt-2">
                <!-- generate due diligence -->
                <div class="switch-check" :style="! shareholder.generateDueDiligence ? 'margin-bottom:10px;' : ''">
                    <b-form-checkbox 
                        v-model="shareholder.generateDueDiligence" 
                        :checked="shareholder.generateDueDiligence" 
                        :checked-value="true"
                        :unchecked-value="false"
                        switch
                        size="md"
                    > 
                        Generate Enhanced Due Diligence (EDD)
                    </b-form-checkbox>
                </div>

                <div v-if="shareholder.generateDueDiligence">
                    <!-- source of wealth -->
                    <b-form-group label="Please select all the relevant source of wealth." class="mt-1">
                        <validation-provider
                            v-slot="{ errors }"
                            name="source of wealth"
                            rules="required"
                        >
                            <b-form-checkbox-group
                                id="checkbox-group-3"
                                ref="sourceOfWealth"
                                v-model="selectedSourcesOfWealth"
                                name="text"
                                stacked
                            >
                                <b-form-checkbox v-for="source in sourceOfFundsOption" :key="source.value" :value="source.value">
                                    {{ source.text }} 

                                    <span v-if="source.tollTopInfo">
                                        <vector
                                            :id="source.tollTopInfo.uniqueId"
                                            data-type="icon"
                                            data-width="13px"
                                            data-height="13px"
                                            data-class="mr-.5"
                                            data-style="margin-bottom:2px;"
                                            data-name="information"
                                        ></vector>

                                        <b-popover
                                            :target="source.tollTopInfo.uniqueId"
                                            triggers="hover"
                                            placement="right"
                                            variant="black"
                                        >
                                           {{ source.tollTopInfo.label }} <br />
                                           
                                            <ul v-if="source.tollTopInfo.option" style="margin:0; padding:0;">
                                                <li v-for="opt in source.tollTopInfo.option" :key="opt" style="margin-left: 14px; padding:0;"> 
                                                    {{ opt }}
                                                </li> 
                                           </ul>
                                        </b-popover>
                                    </span>
                                </b-form-checkbox>
                                
                            </b-form-checkbox-group>

                            <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                                {{ errors[0] }}
                            </div>
                        </validation-provider>
                    </b-form-group>

                    <b-form-group v-if="checkOtherSourceOfWealthOption" data-test="other-source-of-wealth">
                        <validation-provider
                            v-slot="{ errors }"
                            name="other sources of wealth"
                            rules="required"
                        >
                            <b-form-textarea
                                id="funding_other_reasons_of_sow"
                                ref="otherSourceOfWealth"
                                v-model="shareholder.otherWealth"
                                name="other_source_of_wealth"
                                rows="3"
                                max-rows="3"
                                placeholder="Enter your other source of wealth here."
                            ></b-form-textarea>

                            <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                                {{ errors[0] }}
                            </div>
                        </validation-provider>
                    </b-form-group>
                    
                    <!-- source of funds -->
                    <b-form-group label="Please select all the relevant source of funds." style="margin-top:10px;">
                        <validation-provider
                            v-slot="{ errors }"
                            name="source of fund"
                            rules="required"
                        >
                            <b-form-checkbox-group
                                id="checkbox-group-2"
                                ref="sourceOfFund"
                                v-model="selectedSourcesOfFunds"
                                name="sources"
                                stacked
                            >
                                <b-form-checkbox v-for="source in sourceOfFundsOption" :key="source.value" :value="source.value">
                                    {{ source.text }} 

                                    <span v-if="source.tollTopInfo">
                                        <vector
                                            :id="`${source.tollTopInfo.uniqueId}${source.value}`"
                                            data-type="icon"
                                            data-width="13px"
                                            data-height="13px"
                                            data-class="mr-.5"
                                            data-style="margin-bottom:5px;"
                                            data-name="information"
                                        ></vector>

                                        <b-popover
                                            :target="`${source.tollTopInfo.uniqueId}${source.value}`"
                                            triggers="hover"
                                            placement="right"
                                            variant="black"
                                        >
                                            {{ source.tollTopInfo.label }} <br />
                                            
                                            <ul v-if="source.tollTopInfo.option" style="margin:0; padding:0;">
                                                <li v-for="opt in source.tollTopInfo.option" :key="opt" style="margin-left: 13px; padding:0;"> 
                                                    {{ opt }}
                                                </li> 
                                            </ul>
                                        </b-popover>
                                    </span>
                                </b-form-checkbox>

                            </b-form-checkbox-group>
                            <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                                {{ errors[0] }}
                            </div>
                        </validation-provider>
                    </b-form-group>

                    <b-form-group v-if="checkOtherSourceOfFundOption" data-test="other-source">
                        <validation-provider
                            v-slot="{ errors }"
                            name="other sources of fund"
                            rules="required"
                        >
                            <b-form-textarea
                                id="funding_other_reasons"
                                ref="otherSource"
                                v-model="shareholder.otherSource"
                                name="other_source"
                                rows="3"
                                max-rows="3"
                                placeholder="Enter your other source here."
                            ></b-form-textarea>

                            <div v-if="errors[0]" class="alert-dismissable alert alert-danger mt-1 p-1">
                                {{ errors[0] }}
                            </div>
                        </validation-provider>
                    </b-form-group>
                </div>
            </div>
            <!-- End Source of fund and source of wealth -->
           

            <!-- POA -->
            <div v-if="generatePoa && isShareHolderTypeCorporate">
                <validation-provider
                    v-slot="{ errors }"
                    name="primary authorized person name"
                    rules="required"
                >
                    <b-form-group label="Enter primary authorized person name for POA">
                        <b-form-input
                            id="name"
                            v-model="shareholder.POAAuthorizePersonOneName"
                            placeholder="Enter primary authorized person name"
                            :state="errors.length > 0 ? false : null"
                            data-test="primary-authorized-person-name"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="primary authorized person email"
                    rules="required|email"
                >
                    <b-form-group label="Enter primary authorized person email for POA">
                        <b-form-input
                            v-model="shareholder.POAAuthorizePersonOneEmail"
                            placeholder="Enter primary authorized person email"
                            :state="errors.length > 0 ? false : null"
                            data-test="primary-authorized-person-email"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider name="Add Secondary Authorized Person">
                    <b-form-group>
                        <b-form-checkbox 
                            ref="addSecondPOAFields"
                            v-model="shareholder.hasSecondPOA"
                            name="check-button" 
                            style="margin-top:11px;"
                            switch
                            @input="handleSecondaryPOAFields"
                        >
                            Add secondary authorized person
                        </b-form-checkbox>
                    </b-form-group>
                </validation-provider>
                
                <div v-if="shareholder.hasSecondPOA">
                    <validation-provider
                        v-slot="{ errors }"
                        name="secondary authorized person type"
                        rules="required"
                    >
                        <b-form-group :state="errors.length > 0 ? false : null" label="Secondary authorized person type">
                            <b-form-radio-group
                                ref="POAAuthorizedPersonType"
                                v-model="shareholder.POAAuthorizedPersonType"
                                :options="POAAuthorizedPersonTypeOptions"
                                :state="errors.length > 0 ? false : null"
                                name="shareholder.POAAuthorizedPersonType"
                                button-variant="primary"
                                plain
                                data-test="secondary-authorized-person-type"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                    
                    <validation-provider
                        v-slot="{ errors }"
                        name="secondary authorized person name"
                        rules="required"
                    >
                        <b-form-group label="Enter secondary authorized person name for POA">
                            <b-form-input
                                id="name"
                                v-model="shareholder.POAAuthorizePersonTwoName"
                                placeholder="Enter secondary authorized person name"
                                :state="errors.length > 0 ? false : null"
                                data-test="secondary-authorized-person-name"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        v-slot="{ errors }"
                        name="secondary authorized person email"
                        rules="required|email"
                    >
                        <b-form-group label="Enter secondary authorized person email for POA">
                            <b-form-input
                                v-model="shareholder.POAAuthorizePersonTwoEmail"
                                placeholder="Enter secondary authorized person email"
                                :state="errors.length > 0 ? false : null"
                                data-test="secondary-authorized-person-email"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>
            </div>
            <!-- End POA -->

            <div v-if="isShareHolderTypeIndividual" class="address">
                <validation-provider
                    v-slot="{ errors }"
                    name="address"
                    rules="required"
                >
                    <b-form-group>
                        <slot name="label">
                            <label id="shareholder-address" for="id">
                                Address
                                <vector
                                    data-type="icon"
                                    data-width="15px"
                                    data-height="15px"
                                    data-class="mr-.5"
                                    data-name="information"
                                ></vector>
                            </label>
                        </slot>

                        <b-form-textarea
                            id="textarea"
                            v-model="shareholder.address"
                            placeholder="Enter shareholder address"
                            :state="errors.length > 0 ? false : null"
                            data-test="shareholder-address"
                            rows="3"
                            max-rows="3"
                        ></b-form-textarea>

                        <small class="text-danger">{{ errors[0] }}</small>

                        <b-popover
                            target="shareholder-address"
                            triggers="hover"
                            placement="right"
                            variant="black"
                        >
                            Enter Singapore address if you are a Singapore
                            resident.
                            <br />
                            Enter your overseas address if you are a foreigner.
                        </b-popover>
                    </b-form-group>
                </validation-provider>
            </div>

            <div v-if="isShareHolderTypeCorporate" class="corporate-address">
                <validation-provider
                    v-slot="{ errors }"
                    name="primary corporate representative name"
                    rules="required"
                >
                    <b-form-group label="Primary corporate representative name">
                        <b-form-input
                            v-model="
                                shareholder.primaryCorporateRepresentativeName
                            "
                            placeholder="Enter primary corporate representative name"
                            :state="errors.length > 0 ? false : null"
                            data-test="primaryCorporateRepresentativeName"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="primary corporate representative address"
                    rules="required"
                >
                    <b-form-group
                        label="Primary corporate representative address"
                    >
                        <b-form-textarea
                            id="textarea"
                            v-model="
                                shareholder.primaryCorporateRepresentativeAddress
                            "
                            placeholder="Enter primary corporate representative address"
                            :state="errors.length > 0 ? false : null"
                            data-test="primaryCorporateRepresentativeAddress"
                            rows="3"
                            max-rows="3"
                        ></b-form-textarea>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <div
                    v-if="!showSecondaryCorporateRepresentativesOption"
                    class="d-flex justify-content-end align-items-center"
                >
                    <b-button
                        variant="outline-primary"
                        class="d-flex justify-content-end align-items-center"
                        data-test="add-secondary-corporate-representative"
                        @click="
                            showSecondaryCorporateRepresentativesOption = true
                        "
                    >
                        Add secondary corporate representative
                        <vector
                            data-name="add-user"
                            data-width="15px"
                            data-height="12px"
                            class="
                                ml-.5
                                d-flex
                                justify-content-center
                                align-items-center
                            "
                        />
                    </b-button>

                    <vector
                        id="secondary-optional"
                        data-type="icon"
                        data-width="15px"
                        data-height="15px"
                        data-class="ml-1"
                        data-name="information"
                    ></vector>

                    <b-tooltip
                        target="secondary-optional"
                        triggers="hover"
                        placement="top"
                        variant="black"
                    >
                        Secondary corporate representative is optional.
                    </b-tooltip>
                </div>

                <div v-if="showSecondaryCorporateRepresentativesOption">
                    <b-form-group
                        label="Secondary corporate representative name"
                    >
                        <b-form-input
                            v-model="
                                shareholder.secondaryCorporateRepresentativeName
                            "
                            placeholder="Enter secondary corporate representative name"
                            data-test="secondaryCorporateRepresentativeName"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Secondary corporate representative address"
                    >
                        <b-form-textarea
                            id="textarea"
                            v-model="
                                shareholder.secondaryCorporateRepresentativeAddress
                            "
                            placeholder="Enter secondary corporate representative address"
                            data-test="secondaryCorporateRepresentativeAddress"
                            rows="3"
                            max-rows="3"
                        ></b-form-textarea>
                    </b-form-group>
                </div>
            </div>

            <!-- Nominee shareholder nominator section -->
            <div class="d-flex justify-content-between align-items-center">
                <div class="is-nominee">
                    <validation-provider
                        name="nominee shareholder"
                    >
                        <b-form-checkbox 
                            :value="true" 
                            :checked="shareholder.isNomineeShareholder" 
                            @change="handleIsNomineeShareholderOnCheck($event)"
                        >
                            Is nominee shareholder
                        </b-form-checkbox>
                    </validation-provider>
                </div>

                <!-- nominator new or existing -->
                <div v-if="shouldShowNominatorFields" class="d-flex align-items-center mt-1">
                    <b-button
                        variant="outline-primary"
                        class="btn btn-sm d-flex justify-content-end align-items-center mb-1"
                        style="font-size: 1em;"
                        data-test="add-existing-nominator-btn"
                        :disabled="! shouldShowNewNominatorFields"
                        @click="showNominatorField('existing', status)"
                    >
                        Existing Nominator
                        <vector
                            data-name="add-user"
                            data-width="15px"
                            data-height="12px"
                            class="ml-.3 d-flex justify-content-center align-items-center"
                        />
                    </b-button>

                    <b-button
                        variant="outline-primary"
                        class="btn btn-sm d-flex justify-content-end align-items-center mb-1 ml-1"
                        style="font-size: 1em;"
                        data-test="add-new-nominator-btn"
                        :disabled="shouldShowNewNominatorFields"
                        @click="showNominatorField('new', status)"
                    >
                        New Nominator
                        <vector
                            data-name="add-user"
                            data-width="15px"
                            data-height="12px"
                            class=" ml-.3 d-flex justify-content-center align-items-center"
                        />
                    </b-button>
                </div>
                <!-- end nominator new or existing -->
            </div>
            
            <div v-if="shouldShowNominatorFields">
                <validation-provider
                    #default="{ errors }"
                    name="nominator type"
                    rules="required"
                    mode="lazy"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Nominator type"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="nominatorType"
                            v-model="shareholder.nominatorType"
                            class="custom-select-icon text-capitalize"
                            :clearable="false"
                            placeholder="Select nominator type"
                            label="text"
                            :options="nominatorTypeOptions"
                            @input="onNominatorTypeChange"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    #default="{ errors }"
                    name="appointment date"
                    rules="required"
                >
                    <b-form-group label="Date on which the shareholder became the nominee" :state="errors.length > 0 ? false : null">
                        <flat-pickr
                            v-model="shareholder.appointmentDate"
                            placeholder="Pick a date"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-appointment-date"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    #default="{ errors }"
                    name="cessation date"
                >
                    <b-form-group label="Date on which the shareholder ceased to be the nominee" :state="errors.length > 0 ? false : null">
                        <flat-pickr
                            v-model="shareholder.cessationDate"
                            placeholder="Pick a date"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-cessation-date"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <div v-if="! shouldShowNewNominatorFields">
                    <validation-provider
                        #default="{ errors }"
                        :name="dynamicNewNominatorNameLabel.toLowerCase()"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group
                            :label="dynamicNewNominatorNameLabel"
                            :state="errors.length > 0 ? false : null"
                        >
                            <v-select
                                id="name"
                                ref="nominatorName"
                                v-model="shareholder.nominatorName"
                                :placeholder="dynamicNewNominatorPlaceholder"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-name"
                                class="custom-select-icon text-uppercase"
                                :clearable="false"
                                label="text"
                                :options="nominators"
                                @input="onNominatorNameChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <div v-if="shouldShowNewNominatorFields">
                    <validation-provider
                        v-slot="{ errors }"
                        :name="dynamicNominatorIdLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group>
                            <slot name="label">
                                <label id="nominator-id" for="id">
                                    {{ dynamicNominatorIdLabel }}
                                </label>
                            </slot>

                            <b-form-input
                                id="id"
                                v-model="shareholder.nominatorNRIC"
                                :state="errors.length > 0 ? false : null"
                                :placeholder="dynamicNominatorIdPlaceholder"
                                trim
                                data-test ="nominator-nric"
                                @input="requestAutoFillNominatorFormData"
                            />

                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <div v-if="isNominatorTypeIndividual">
                        <validation-provider
                            v-slot="{ errors }"
                            name="id type"
                            rules="required"
                            class="end-align-indicator-long-select"
                        >
                            <b-form-group label="ID type" :state="errors.length > 0 ? false : null">
                                <v-select
                                    ref="nominatorIdType"
                                    v-model="shareholder.nominatorIdType"
                                    class="custom-select-icon"
                                    :clearable="false"
                                    placeholder="Select ID type"
                                    label="name"
                                    :options="idType_options"
                                    @input="(idType) => handleIdTypeOnChange(idType, 'nominatorNationality')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="nominator nationality"
                            rules="required"
                            class="end-align-indicator-long-select"
                        >
                            <b-form-group label="Nationality" :state="errors.length > 0 ? false : null" >
                                <v-select
                                    id="nominator-nationality"
                                    ref="nominatorNationality"
                                    v-model="shareholder.nominatorNationality"
                                    class="custom-select-icon"
                                    label="text" 
                                    placeholder="Select nationality"
                                    :clearable="false"
                                    :options="nationality_options"
                                    :disabled ="shouldDisabledNewNominatorNationality"
                                >
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <div v-if="shouldHideNominatorDateOfExpiry">
                            <validation-provider
                                v-slot="{ errors }"
                                name="nominator date of expiry"
                                rules="required"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null">
                                    <slot name="label">
                                        <label id="dateOfExpiry" for="id">
                                            Date of expiry
                                            <vector
                                                data-type="icon"
                                                data-width="15px"
                                                data-height="15px"
                                                data-class="mr-.5"
                                                data-name="information"
                                            ></vector>
                                        </label>
                                    </slot>

                                    <b-popover
                                        target="dateOfExpiry"
                                        triggers="hover"
                                        placement="right"
                                        variant="black"
                                    >
                                        Skip this if you are a Singapore citizen or permanent
                                        resident.
                                        <br />
                                        Fill in the date of expiry of your passport or FIN if
                                        you are a foreigner.
                                    </b-popover>

                                    <flat-pickr
                                        v-model="shareholder.nominatorDateOfExpiry"
                                        placeholder="Enter date of expiry"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        data-test="date-picker-nominator-doe"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>
                    </div>

                    <validation-provider 
                        v-slot="{ errors }"
                        :name="dynamicNewNominatorNameLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group :label="dynamicNewNominatorNameLabel">
                            <b-form-input
                                id="name"
                                v-model="shareholder.newNominatorName"
                                :placeholder="dynamicNewNominatorPlaceholder"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-name"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <!-- < INDIVIDUAL NOMINATOR > -->
                <div v-if="isNominatorTypeIndividual">
                    <validation-provider
                        #default="{ errors }"
                        name="individual nominator aliases"
                    >
                        <b-form-group label="Individual nominator aliases, if any">
                            <b-form-input
                                id="name"
                                v-model="shareholder.nominatorAlias"
                                placeholder="individual nominator aliases"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-alias"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>
                <!-- </ INDIVIDUAL NOMINATOR > -->

                <div v-if="shouldShowNewNominatorFields">
                    <div v-if="isNominatorTypeIndividual">
                        <validation-provider
                            v-slot="{ errors }"
                            name="date of birth"
                            rules="required"
                        >
                            <b-form-group label="Date of birth" :state="errors.length > 0 ? false : null">
                                <flat-pickr
                                    v-model="shareholder.nominatorDOB"
                                    class="form-control"
                                    data-test="nominator-date-of-birth"
                                    placeholder="Enter date of birth"
                                    :config="datePickerConfig"
                                    :state="errors.length > 0 ? false : null"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>
                    </div>

                    <validation-provider
                        v-slot="{ errors }"
                        :name="dynamicNominatorAddressLabel.toLowerCase()"
                        rules="required"
                    >
                        <b-form-group :label="dynamicNominatorAddressLabel">
                            <b-form-textarea
                                id="textarea"
                                v-model="shareholder.nominatorAddress"
                                :placeholder="dynamicNominatorAddressPlaceholder"
                                :state="errors.length > 0 ? false : null"
                                trim
                                rows="3"
                                max-rows="3"
                                data-test="nominator-address"
                            >
                            </b-form-textarea>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                </div>

                <!-- < CORPORATE NOMINATOR > -->
                <div v-if="! isNominatorTypeIndividual">
                    <validation-provider #default="{ errors }"
                        name="corporate nominator UEN"
                        rules="required"
                    >
                        <b-form-group label="Corporate nominator UEN issued by the registrar (ACRA)">
                            <b-form-input
                                id="name"
                                v-model="shareholder.nominatorUen"
                                placeholder="Enter corporate nominator UEN"
                                :state="errors.length > 0 ? false : null"
                                data-test="nominator-uen"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider
                        #default="{ errors }"
                        name="corporate nominator legal form"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group
                            label="Corporate nominator legal form"
                            :state="errors.length > 0 ? false : null"
                        >
                            <v-select
                                ref="legalForm"
                                v-model="shareholder.nominatorLegalForm"
                                placeholder="Select corporate nominator legal form"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="text"
                                :options="legalForms"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="corporate nominator jurisdiction"
                        rules="required"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Corporate nominator jurisdiction" :state="errors.length > 0 ? false : null">
                                <v-select
                                ref ="jurisdiction"
                                v-model="shareholder.nominatorJurisdiction"
                                :clearable="false"
                                :options="jurisdictions"
                                label="name"
                                value="id"
                                class="custom-select-icon text-capitalize"
                                placeholder="Select corporate nominator jurisdiction"
                                @input="onNominatorJurisdictionChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="corporate nominator statue"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group label="Corporate nominator statue under which the corporate is formed" :state="errors.length > 0 ? false : null">
                            <v-select
                                ref="statue"
                                v-model="shareholder.nominatorStatue"
                                placeholder="Select corporate nominator statue"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="name"
                                value="id"
                                :options="statues"
                                :state="errors.length > 0 ? false : null"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider #default="{ errors }"
                        name="registrar"
                        rules="required"
                        mode="lazy"
                        class="end-align-indicator-long-select"
                    >
                        <b-form-group :state="errors.length > 0 ? false : null" label="Name of corporate nominator's register of the jurisdiction where the legal entity is formed or incorporated">
                            <v-select
                                ref="nominatorRegistrar"
                                v-model="shareholder.nominatorRegistrar"
                                class="custom-select-icon text-capitalize"
                                :clearable="false"
                                label="name"
                                value="id"
                                :options="registrars"
                                :state="errors.length > 0 ? false : null"
                                placeholder="Select corporate registrar"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <validation-provider
                    #default="{ errors }"
                    name="remark"
                >
                    <b-form-group label="Remarks">
                        <b-form-input
                            id="remark"
                            v-model="shareholder.remark"
                            placeholder="Enter nominee shareholder remark"
                            :state="errors.length > 0 ? false : null"
                            data-test="remark"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>
            <!-- End Nominee shareholder nominator section -->


        </validation-observer>

        <b-button block class="mt-2" variant="primary" data-test="create-or-edit-shareholder-btn" @click="createOrUpdate"
            >{{ status === "create" ? "Add" : "Update" }}
            <img
                v-if="status === 'create'"
                :src="'/illustrations/add-circle.svg'"
                class="float-right"
                alt="add-logo"
            />
            <img
                v-if="status === 'edit'"
                :src="'/illustrations/edit.svg'"
                class="float-right"
                alt="edit-logo"
            />
        </b-button>

        <b-button
            block
            class="mb-3"
            variant="outline-primary"
            data-test="cancel"
            @click="$emit('cancel')"
            >Cancel
            <!-- <img
                :src="'/illustrations/cancel-circle.svg'"
                class="float-right"
                alt="cancel-logo"
            /> -->
            <vector 
                data-type="icons" 
                data-width="15px" 
                data-height="15px" 
                data-name="cancel" 
                class="float-right"
                alt="cancel-logo"
            />
        </b-button>
    </div>
</template>

<script>
import VueSlideBar from "vue-slide-bar";
import {
    BFormGroup,
    BFormInput,
    BButton,
    BFormTextarea,
    BFormRadioGroup,
    BFormCheckbox,
    BPopover,
    BTooltip,
    BFormCheckboxGroup,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { debounce } from 'debounce';
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { oneOf } from 'vee-validate/dist/rules';
import { required, email } from "@validations";
import IdType from "@/models/IdType";
import Nationality from "@/models/Nationality";
import Country from "@/models/Country";
import ShareType from "@/models/ShareType";
import User from '@/models/User';
import { getSourceOfFundsAndReasonsToIncorporate } from "@/apis/company";

import { 
    SHAREHOLDER,
    DIRECTORS_AND_SHAREHOLDERS,
    SECRETARIES,
    SLIDER_CONFIG, 
    NRIC_CITIZEN_ID,
    SINGAPORE_CITIZEN,
    NOMINEE_SHAREHOLDER,
    SOW_AND_SOF_TOOLTIP_INFO
} from "@/constants/config";
import { formatDate, shareMask, unMask } from "@/helpers";
import CompanyNomineeShareholder from "@/models/CompanyNomineeShareholder";
import Nominator from "@/models/Nominator";

extend('formation_validate_share', (value, parameter) => {
    // ${totalNumberOfShare},${dataStatus},${companyTotalShare},${dataShareholder.noOfShares}
    let totalNumberOfShare = unMask(parameter[0]);
    const status = parameter[1];
    const companyTotalShare = parameter[2];
    const oldValue = parameter[3];

    if (status === 'edit')
        totalNumberOfShare -= oldValue;

    if (unMask(value) <= 0)
        return 'Number of shares must be greater than 0.';
   
    if ((companyTotalShare - totalNumberOfShare) < unMask(value))
    {
        return `Number of shares cannot exceed unallocated share count of ${companyTotalShare - totalNumberOfShare}.`;
    }

    return true;
});

export default {
    components: {
        BFormGroup,
        BFormInput,
        BButton,
        ValidationObserver,
        ValidationProvider,
        flatPickr,
        vSelect,
        BFormTextarea,
        BFormRadioGroup,
        BFormCheckbox,
    
        BPopover,
        BTooltip,
        BFormCheckboxGroup,
        VueSlideBar,
    },

    props: {
        hasNomineeDirector: {
            type: Boolean,
            default: false,
        },

        generatePoa: {
            type: Boolean,
            default: false,
        },

        dataStatus: {
            required: false,
            default: "create",
            type: String,
        },

        dataShareholder: {
            required: true,
            type: Object,
        },

        totalNumberOfShare: {
            required: true,
            type: Number
        },

        companyTotalShare: {
            required: true,
            type: Number
        },

        allShareholders: {
            required: true,
            type: Array,
        },
        dataDirectors: {
            required: true,
            type: Array,
        }
    },

    data() {
        return {
            required,
            email,
            oneOf,

            defaultKey: 0,
            status: this.dataStatus,
            CORPORATE_TYPE: SHAREHOLDER.TYPE.CORPORATE,
            showSecondaryCorporateRepresentativesOption: false,

            POAAuthorizedPersonTypeOptions: [
                { text: 'Director', value: DIRECTORS_AND_SHAREHOLDERS.DIRECTOR_NAME },
                { text: 'Secretary', value: SECRETARIES.SECRETARY_NAME },
            ],

            shareholder: {
                // eslint-disable-next-line no-underscore-dangle
                _id: this.dataShareholder._id,
                shareholderType: this.dataShareholder.shareholderType,
                name: this.dataShareholder.name,
                idType: this.dataShareholder?.idType,
                nric: this.dataShareholder.nric,
                nationality: this.dataShareholder.nationality,
                dateOfExpiry: this.dataShareholder.dateOfExpiry,
                country: this.dataShareholder.country,
                dob: this.dataShareholder.dob,
                email: this.dataShareholder.email,
                contactNumber: this.dataShareholder.contactNumber,
                noOfShares: this.dataShareholder.noOfShares,
                sharePercentage: this.dataShareholder.sharePercentage? parseFloat(this.dataShareholder.sharePercentage).toFixed(2) : 0,
                shareTypeId: this.dataShareholder.shareTypeId,
                generateDueDiligence:  this.dataShareholder.generateDueDiligence,
                sourcesOfFunds: this.dataShareholder.sourcesOfFunds,
                otherSource: this.dataShareholder.otherSource,
                sourceOfWealth: this.dataShareholder.sourceOfWealth,
                otherWealth: this.dataShareholder.otherWealth,
                address: this.dataShareholder.address,
                POAAuthorizePersonOneName: this.dataShareholder.POAAuthorizePersonOneName,
                POAAuthorizePersonOneEmail: this.dataShareholder.POAAuthorizePersonOneEmail,
                hasSecondPOA: this.dataShareholder.hasSecondPOA,
                POAAuthorizePersonTwoName: this.dataShareholder.POAAuthorizePersonTwoName,
                POAAuthorizePersonTwoEmail: this.dataShareholder.POAAuthorizePersonTwoEmail,
                POAAuthorizedPersonType: this.dataShareholder.POAAuthorizedPersonType,
                primaryCorporateRepresentativeName:
                    this.dataShareholder.primaryCorporateRepresentativeName,
                primaryCorporateRepresentativeAddress:
                    this.dataShareholder.primaryCorporateRepresentativeAddress,
                secondaryCorporateRepresentativeName:
                    this.dataShareholder.secondaryCorporateRepresentativeName,
                secondaryCorporateRepresentativeAddress:
                    this.dataShareholder.secondaryCorporateRepresentativeAddress,

                // nominee shareholder nominator fields
                isNomineeShareholder: this.dataShareholder.isNomineeShareholder ?? false,
                isNewNominator: this.dataShareholder.isNewNominator ?? false ,
                appointmentDate: this.dataShareholder.appointmentDate,
                cessationDate: this.dataShareholder.cessationDate,
                nominatorType:this.dataShareholder.nominatorType,
                nominatorName: this.dataShareholder.nominatorName,
                nominatorAlias: this.dataShareholder.nominatorAlias,
                // new nominator field
                newNominatorName: this.dataShareholder.newNominatorName,
                nominatorNRIC: this.dataShareholder.nominatorNRIC,
                nominatorIdType: this.dataShareholder.nominatorIdType,
                nominatorNationality: this.dataShareholder.nominatorNationality,
                nominatorDateOfExpiry: this.dataShareholder.nominatorDateOfExpiry,
                nominatorDOB: this.dataShareholder.nominatorDOB,
                nominatorAddress: this.dataShareholder.nominatorAddress,
                nominatorEmail: this.dataShareholder.nominatorEmail,
                // end new nominator field
                nominatorUen: this.dataShareholder.nominatorUen,
                nominatorStatue: this.dataShareholder.nominatorStatue,
                nominatorLegalForm: this.dataShareholder.nominatorLegalForm,
                nominatorJurisdiction: this.dataShareholder.nominatorJurisdiction,
                nominatorRegistrar: this.dataShareholder.nominatorRegistrar,
                remark: this.dataShareholder.remark,
            },

            shareTypeDefaultValue: undefined,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            sourceOfFundsOption: [],
            selectedSourcesOfFunds: [],
            selectedSourcesOfWealth: [],
            nationality_options: [],
            country_options: [],
            idType_options: [],
            shareholderOptions: [
                {
                    text: SHAREHOLDER.TYPE.INDIVIDUAL,
                    value: SHAREHOLDER.TYPE.INDIVIDUAL,
                },
                {
                    text: SHAREHOLDER.TYPE.CORPORATE,
                    value: SHAREHOLDER.TYPE.CORPORATE,
                },
            ],
            shareTypes: [],

            sliderConfig: SLIDER_CONFIG,

            maskKeys: ['noOfShares'],
            shareMask,

            // nominee shareholder
            nominatorTypeOptions: [],

            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            nominators: [],
            jurisdictions: [],

            sowAndSofToolTipInfo: SOW_AND_SOF_TOOLTIP_INFO,
        };
    },

    computed: {
        checkOtherSourceOfFundOption() {
            if (this.selectedSourcesOfFunds.length >= 1) {
                const otherSource = this.sourceOfFundsOption.find(
                    reason => reason.text.search(/^Others/i) === 0
                );
                
                if(otherSource) 
                    return this.selectedSourcesOfFunds.includes(otherSource.value);
            }

            return false;
        },

        checkOtherSourceOfWealthOption() {
            if (this.selectedSourcesOfWealth.length >= 1) {
                const otherWealth = this.sourceOfFundsOption.find(
                    reason => reason.text.search(/^Others/i) === 0
                );
                
                if(otherWealth) 
                    return this.selectedSourcesOfWealth.includes(otherWealth.value);
            }

            return false;
        },

        doesNeedPOA() 
        {
            return this.hasNomineeDirector && this.isShareHolderTypeCorporate;
        },
        
        isShareHolderTypeCorporate() 
        {
            // if there is no type yet, act like it's Individual
            if (!this.shareholder.shareholderType) return false;

            return (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text === SHAREHOLDER.TYPE.CORPORATE
            );
        },

        isShareHolderTypeIndividual() 
        {
            // if there is no type yet, act like it's Individual
            if (!this.shareholder.shareholderType) return true;

            return (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.INDIVIDUAL
            );
        },

        dynamicIdLabel() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Registration number";

            return "ID";
        },

        dynamicIdPlaceholder() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Enter registration number";

            return "Enter NRIC or passport";
        },

        dynamicDobLabel() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Incorporation date";

            return "Date of birth";
        },

        dynamicDobPlaceholder() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Enter incorporation date";

            return "Enter date of birth";
        },

        dynamicEmailLabel() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Corporate representative email";

            return "Email";
        },

        dynamicEmailPlaceholder() {
            if (
                this.shareholder.shareholderType &&
                this.shareholder.shareholderType.text ===
                    SHAREHOLDER.TYPE.CORPORATE
            )
                return "Enter corporate representative email";

            return "Enter email";
        },

        shouldDisabledNationality() {
            return this.shareholder?.idType?.id === NRIC_CITIZEN_ID;
        },

        shouldHideShareholderDateOfExpiry() {
            if(this.shareholder.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.shareholder.dateOfExpiry = null;
            }
            return ! this.shareholder.idType?.name.includes("NRIC");
        },

        // nominee shareholder nominator section
        shouldShowNominatorFields() {
            return this.shareholder.isNomineeShareholder;
        },
        
        shouldShowNewNominatorFields() {
            return this.shareholder.isNewNominator;
        },

        isNominatorTypeIndividual() {
            // if there is no type yet, act like it's Individual
            if (! this.shareholder.nominatorType) return true;

            return (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL
            );
        },

        dynamicNominatorIdLabel() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator id on the corporate entity register of the jurisdiction where the corporate controller is formed or incorporated";

            return "Individual nominator ID";
        },

        dynamicNominatorIdPlaceholder() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator id";

            return "Enter individual nominator id";
        },

        dynamicNewNominatorNameLabel() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Name";
        },

        dynamicNewNominatorPlaceholder() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator name";

            return "Enter nominator name";
        },

        dynamicNominatorAddressLabel() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Address of registered office";

            return "Address";
        },

        dynamicNominatorAddressPlaceholder() {
            if (
                this.shareholder.nominatorType &&
                this.shareholder.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter address of registered office";

            return "Enter address";
        },

        shouldDisabledNewNominatorNationality() {
            return this.shareholder?.nominatorIdType?.id === NRIC_CITIZEN_ID;
        },

        shouldHideNominatorDateOfExpiry() {
            if(this.shareholder.nominatorIdType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.shareholder.nominatorDateOfExpiry = null;
            }

            return ! this.shareholder.nominatorIdType?.name.includes("NRIC");
        },
        // end nominee shareholder nominator section
    },

    async created() {
        // !! need to load source of funds first
        // source of funds
        getSourceOfFundsAndReasonsToIncorporate()
            .then(data => {
                this.sourceOfFundsOption = this.getSourceOfFunds(data);
                if(Array.isArray(this.shareholder.sourcesOfFunds)) {
                    this.selectedSourcesOfFunds = [...new Set([...this.selectedSourcesOfFunds, ...this.shareholder.sourcesOfFunds])];
                    this.selectedSourcesOfWealth = [...new Set([...this.selectedSourcesOfWealth, ...this.shareholder.sourceOfWealth])];
                }
            })
            .catch(e => {
                console.log("Error while trying to source of funds reasons to incorporate", e);
            });
            
        const nationalityResponse = await Nationality.all();
        const countryResponse = await Country.all();
        const shareTypesResponse = await ShareType.all();
        this.idType_options = await IdType.all();

        this.nationality_options = nationalityResponse.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });

        this.country_options = countryResponse.data.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });

        this.shareTypes = shareTypesResponse.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });

        this.shareTypeDefaultValue = this.shareTypes[0].text;

        if (this.shareholder.secondaryCorporateRepresentativeName)
            this.showSecondaryCorporateRepresentativesOption = true;

        // nominee shareholder nominator section
        if(! this.dataShareholder.isNewNominator)
            this.shareholder.isNewNominator = false;

        if(this.shareholder)
            this.reInitializeNominators(this.shareholder.nominatorType ? this.shareholder.nominatorType.text : NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL);

        const payload = await Nominator.getCorporateData();
        
        this.jurisdictions = payload.jurisdiction;
        this.statues = payload.statue;
        this.dataStatues = this.statues;
        this.legalForms = payload.legal_form;
        this.registrars = payload.registrar;
        this.dataRegistrars = this.registrars;

        // get nominator type options
        const nominatorTypeOptionsData = await CompanyNomineeShareholder.getNominatorTypeOptions();
        this.nominatorTypeOptions = nominatorTypeOptionsData.map(opt => {
            const text  = opt;
            const value  = opt;
            return { text, value };
        });
        // end nominee shareholder nominator section
    },

    methods: {
        getSourceOfFunds(payload) {
            return payload
                .filter(info => info.type === "Source")
                .map(data => ({
                    text: data.description,
                    value: data.id,
                    tollTopInfo: this.getTollTipInfo(data),
                }));
        },

        getTollTipInfo(data) {
            return this.sowAndSofToolTipInfo.find(opt => opt.otherSourceId === data.id);
        },

        handleSecondaryPOAFields(val) {
            this.shareholder.hasSecondPOA = val;
        },

        handleShareholderTypeChange()
        {
            this.$refs.createShareholderValidation.reset();
            this.defaultKey += 1;
            if (this.isShareHolderTypeCorporate) {
                this.shareholder.idType = null;
                this.shareholder.nationality = null;

                if(this.dataShareholder && this.dataShareholder?.country)
                    this.shareholder.country = this.dataShareholder.country;
                else this.shareholder.country = null;
            }

            if (this.isShareHolderTypeIndividual) {
                this.shareholder.country = null;
                
                this.shareholder.idType = this.dataShareholder.idType;
                this.shareholder.nationality = this.dataShareholder.nationality;
            }
        },

        calculateSharePercentage(shareInNumber)
        {
            let percentage = (Number(shareInNumber.replace(/[$,]/g, '')) / this.companyTotalShare) * 100;

            if (percentage % 10 !== 0)
                percentage = percentage.toFixed(2);

            this.shareholder.sharePercentage = percentage;
        },

        handleIdTypeOnChange(data, nationalityKey = 'nationality')
        {
            if(data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationality_options.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];
                
                this.shareholder[nationalityKey] = singaporeCitizen;
            }
        },

        // eslint-disable-next-line func-names
        requestAutoFillFormData: debounce(async function(id) {
            if (id && this.shareholder.shareholderType?.text) {
                const user = await User.getByNRIC(id, this.shareholder.shareholderType.text);

                if(user) this.fillShareholderData(user);
            }
        }, 500),

        fillShareholderData(user) {
            this.shareholder.name = user?.user_info?.legal_name ?? user.name;

            if(this.isShareHolderTypeIndividual) 
            {
                this.shareholder.idType = user.user_info?.id_type
                    ? {
                        id: user.user_info.id_type.id,
                        name: user.user_info.id_type.name
                    }
                    : null;
                this.shareholder.nationality = user.user_info?.nationality
                    ? { 
                        value: user.user_info.nationality.id,
                        text: user.user_info.nationality.name 
                    }
                    : null;
                this.shareholder.dateOfExpiry = formatDate(user.user_info.date_of_expiry);
            }

            if(this.isShareHolderTypeCorporate)
            {
                this.shareholder.country = user.user_info?.country
                    ? { 
                        value: user.user_info.country.id,
                        text: user.user_info.country.name 
                    }
                    : null;

                if(user?.shareholder?.corporate_representative)
                {
                    this.shareholder.primaryCorporateRepresentativeName = user?.shareholder?.corporate_representative[0]?.name;
                    this.shareholder.primaryCorporateRepresentativeAddress = user?.shareholder?.corporate_representative[0]?.address;

                    
                    this.showSecondaryCorporateRepresentativesOption = !!user.shareholder.corporate_representative[1]?.name;
                        
                    this.shareholder.secondaryCorporateRepresentativeName = user.shareholder.corporate_representative[1]?.name;
                    this.shareholder.secondaryCorporateRepresentativeAddress = user.shareholder.corporate_representative[1]?.address;
                }
                else 
                {
                    this.showSecondaryCorporateRepresentativesOption = false;

                    this.shareholder.primaryCorporateRepresentativeName = null;
                    this.shareholder.primaryCorporateRepresentativeAddress = null;
                    this.shareholder.secondaryCorporateRepresentativeName = null;
                    this.shareholder.secondaryCorporateRepresentativeAddress = null;
                }
            }
                
            this.shareholder.nric = user.user_info?.NRIC;
            this.shareholder.dob = formatDate(user.user_info?.DOB);
            this.shareholder.email = user.email;
            this.shareholder.contactNumber = user.user_info?.contact_number;
            this.shareholder.address = user.user_info?.address;
        },

        // nominee shareholder section
        // nominee shareholder region
        handleIsNomineeShareholderOnCheck(check) {
            this.shareholder.isNomineeShareholder = check;
        },

        async onNominatorTypeChange(payload)
        {
            this.resetNominatorLocalFields();
            this.reInitializeNominators(payload.value);
            this.shareholder.nominatorName = undefined;
            this.shareholder.nominatorUen = undefined;
        },

        reInitializeNominators(type)
        {
            const nominatorEmails = [];
            
            if(this.shareholder.email) {
                this.nominators = this.allShareholders.filter(shareholder => 
                    shareholder.shareholderType.text === type && this.shareholder.email.toLowerCase() !== shareholder.email.toLowerCase()
                ).map(shareholder => {
                    nominatorEmails.push(shareholder.email.toLowerCase());

                    return {
                        _idForShareholder: shareholder._id,
                        value: shareholder.referenceId,
                        text: shareholder.name,
                        email: shareholder.email,
                        nric: shareholder.nric
                    };
                });
            }
            else {
                this.nominators = this.allShareholders.filter(
                    shareholder => shareholder.shareholderType.text === type
                ).map(shareholder => {
                    nominatorEmails.push(shareholder.email.toLowerCase());

                    return {
                        _idForShareholder: shareholder._id,
                        value: shareholder.referenceId,
                        text: shareholder.name,
                        email: shareholder.email,
                        nric: shareholder.nric
                    };
                });
            }

            // add director two nominator options list if not exit
            if (type === SHAREHOLDER.TYPE.INDIVIDUAL) {
                this.dataDirectors.forEach(director => {
                    if(! nominatorEmails.includes(director.email.toLowerCase()) && this.shareholder.email?.toLowerCase() !== director.email.toLowerCase()) {
                        this.nominators.push({
                            value: director.userId,
                            text: director.name,
                            email: director.email,
                            nric: director.nric
                        });
                    }
                });
            }
        },

        showNominatorField (value) {
            if(value === 'new') {
                this.resetNominatorLocalFields();
                this.shareholder.isNewNominator = true;
            }

            if(value === 'existing') {
                this.resetNominatorLocalFields();

                this.shareholder.isNewNominator = false;
            }
        },

        onShareholderEmailChange(shareholderEmail) {
            const sameNominator = this.allShareholders.filter(shareholder => 
                shareholderEmail.toLowerCase() === shareholder.email.toLowerCase()
            ).length > 0;

            if(sameNominator)
                this.shareholder.nominatorName = undefined;

            this.reInitializeNominators(this.shareholder.nominatorType?.text);
        },

        onNominatorJurisdictionChange(payload)
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === payload.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === payload.id);
            this.shareholder.nominatorStatue = '';
            this.shareholder.nominatorRegistrar = '';
        },

        onNominatorNameChange(nominator) {
            this.requestAutoFillNominatorFormData(nominator.nric);
        },

        // eslint-disable-next-line func-names
        requestAutoFillNominatorFormData: debounce(async function(id) {
            const type = this.shareholder?.nominatorType?.text;

            if(type && id) {
                const user = await User.getByNRIC(id, type);

                if(! user) this.shareholder.nominatorEmail = undefined;

                if(user) this.fillNominatorData(user);
            }
        }, 500),

        fillNominatorData(nominator) {
            if(this.shouldShowNominatorFields) {
                if (nominator.user_info?.id_type) {
                    this.shareholder.nominatorIdType = {
                        id: nominator.user_info.id_type?.id,
                        name: nominator.user_info.id_type?.name
                    };
                }
                if (nominator.user_info?.nationality) {
                    this.shareholder.nominatorNationality = {
                        text: nominator.user_info.nationality?.name,
                        value: nominator.user_info.nationality?.id
                    };
                }
                
                this.shareholder.nominatorDateOfExpiry = formatDate(nominator.user_info?.date_of_expiry);
               
                this.shareholder.nominatorNRIC = nominator.user_info?.NRIC;
                this.shareholder.newNominatorName = nominator?.user_info?.legal_name ?? nominator?.name;
                
                // just sure backend not generate email for new nominator
                this.shareholder.nominatorEmail = nominator?.email;
                
                this.shareholder.nominatorDOB = formatDate(nominator?.user_info?.DOB);
                this.shareholder.nominatorAddress = nominator?.user_info?.address;

                if
                (
                    ! nominator?.user_info?.nationality 
                    && nominator?.user_info?.id_type?.name === "NRIC (Citizen)"
                )
                {
                    this.shareholder.nominatorNationality = { value: 169, text: "SINGAPORE CITIZEN" };
                }
            }

            if(this.isNominatorTypeIndividual) 
                this.shareholder.nominatorAlias = nominator?.individual_shareholder?.alias;

            if(! this.isNominatorTypeIndividual) {
                this.shareholder.nominatorUen = nominator?.user_info?.uen;
                this.shareholder.nominatorStatue = nominator?.shareholder?.statue;
                this.shareholder.nominatorRegistrar = nominator?.shareholder?.registrar;
                this.shareholder.nominatorJurisdiction = nominator?.shareholder?.jurisdiction;
                this.shareholder.nominatorLegalForm = nominator?.shareholder?.legal_form;
            }
        },

        resetNominatorLocalFields() {
            this.shareholder.nominatorName = undefined;
            this.shareholder.newNominatorName = undefined;
            this.shareholder.nominatorNRIC = undefined;
            this.shareholder.appointmentDate = undefined;
            this.shareholder.cessationDate = undefined;
            this.shareholder.nominatorDOB = undefined;
            this.shareholder.nominatorIdType = undefined;
            this.shareholder.nominatorNationality = undefined;
            this.shareholder.nominatorAddress = undefined;
            this.shareholder.nominatorAlias = undefined;
            this.shareholder.nominatorEmail = undefined;
            this.shareholder.nominatorUen = undefined;
            this.shareholder.nominatorJurisdiction = undefined;
            this.shareholder.nominatorStatue = undefined;
            this.shareholder.nominatorRegistrar = undefined;
            this.shareholder.nominatorLegalForm = undefined;
            this.shareholder.remark = undefined;
        },
        // end nominee shareholder section

        createOrUpdate() {
            this.$refs.createShareholderValidation
                .validate()
                .then(async success => {
                    if (success) {
                        this.shareholder.sourcesOfFunds = this.selectedSourcesOfFunds;
                        this.shareholder.sourceOfWealth = this.selectedSourcesOfWealth;

                        this.$emit("createOrEdit", unMask(this.shareholder, this.maskKeys));
                        this.$emit("cancel");
                    }
                });
        },
    },
};
</script>

<style scoped>
    .sof-options {
        margin-top: 1px;
    }
</style>